import { useCallback, useMemo, useRef, useState } from 'react';
import type { IDashboardColumn } from 'models';

/**
 * ColumnSettingsModal — "Manage columns" (§3.G — `view/settings.js` +
 * `settings.tmpl` + `selected.tmpl`). Opened via ContainerHeader `.setting-btn`.
 *
 *   div#whiteboard_sett.popup-wrapper.width-medium.active > .popup-content
 *     ├── h2.popup-heading "Manage columns"
 *     ├── ul.two-heading (li>h3 "Available columns", li>h3 "Visible columns")
 *     ├── .popup-content-part.v-brd.two-cols-popup-content
 *     │   ├── ul (left: li>label[for=ch{id}]>input#ch{id}[checkbox name={id} checked?]
 *     │   │        + span.label.text-left {name})
 *     │   └── ul#selected_columns (right: li.clearfix.drag[name={id}] > span {name}
 *     │        + i.icon-two-lines)
 *     ├── .popup-note "Move up … move left … move down … move right …"
 *     └── .popup-control-buttons (button[name=done].highlighted-btn "Done",
 *         button[name=cancel].not-highlighted-btn "Cancel")
 *
 * NOTE: the real OLD `settings.tmpl` (whiteboard) has NO `.icon-lost` close
 * icon — only the Done + Cancel buttons. (The inventory §3.G's '.icon-lost'
 * entry does not match the live Backbone template, so it is intentionally not
 * rendered here.)
 *
 * Behavior (parity 52-58):
 *  - Available list = every column EXCEPT the fixed `project_name` (parity 53);
 *    checked iff the id is in the current visible set.
 *  - Init visible set = dedup `parseInt` of `user.manage_columns` comma-split;
 *    else ALL available ids (§3.G init).
 *  - Checking an available column PREPENDS its id to the visible list top
 *    (unshift); unchecking removes it (parity 54).
 *  - Visible list is draggable to reorder; UP = left in the dashboard, DOWN =
 *    right (parity 55) — see the popup note.
 *  - Done → if the ordered visible ids changed, persist `user.manage_columns`
 *    (comma-joined) via updateUser + rebuild the table (slice manageColumns)
 *    (parity 57). Cancel discards (parity 57).
 *  - Esc closes the modal: settings.js's OWN `keyup body` handler is commented
 *    out (§3.G), but the GLOBAL MainView body keyup handler (main.js) fires for
 *    ANY `.popup-wrapper.active` — on Esc it clicks `[name=cancel]` and removes
 *    the popup. So OLD closes Manage Columns on Esc; the shared popup key
 *    handler (usePopupKeys) does the same (parity 126).
 */
interface ColumnSettingsModalProps {
  /** Role-filtered column defs (GET /projects/columns/). */
  columns: IDashboardColumn[] | undefined;
  /** The current visible-column id order (slice manageColumns; empty ⇒ all). */
  manageColumns: number[];
  /** Persist the new order (comma-joined) to user.manage_columns + rebuild. */
  onDone: (orderedIds: number[]) => void;
  /** Discard + close. */
  onCancel: () => void;
}

const ColumnSettingsModal = ({
  columns,
  manageColumns,
  onDone,
  onCancel,
}: ColumnSettingsModalProps) => {
  // Available = every column except the fixed `project_name` (parity 53).
  const available = useMemo<IDashboardColumn[]>(
    () => (columns ?? []).filter((c) => c.value !== 'project_name'),
    [columns]
  );
  const availableIds = useMemo(() => available.map((c) => Number(c.id)), [available]);
  const nameById = useMemo(() => {
    const m = new Map<number, string>();
    available.forEach((c) => m.set(Number(c.id), c.name));
    return m;
  }, [available]);

  // Initial visible set: dedup manage_columns, filtered to ids that still exist
  // in the available set; else ALL available ids (§3.G init).
  const initialSelected = useMemo<number[]>(() => {
    const valid = new Set(availableIds);
    if (manageColumns && manageColumns.length > 0) {
      const seen = new Set<number>();
      const out: number[] = [];
      for (const id of manageColumns) {
        const n = Number(id);
        if (valid.has(n) && !seen.has(n)) {
          seen.add(n);
          out.push(n);
        }
      }
      return out;
    }
    return [...availableIds];
  }, [manageColumns, availableIds]);

  // Working copy of the visible-column id order; committed on Done.
  const [selected, setSelected] = useState<number[]>(initialSelected);

  // Drag state for the visible list.
  const dragIdRef = useRef<number | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);

  const isChecked = useCallback((id: number) => selected.includes(id), [selected]);

  // change checkbox → select_columns: checked unshifts to FRONT (top); unchecked
  // removes (parity 54).
  const toggle = useCallback((id: number) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [id, ...prev];
    });
  }, []);

  // ---- Drag-reorder of the visible list (native HTML5 DnD) ----
  const onDragStart = useCallback((e: React.DragEvent, id: number) => {
    dragIdRef.current = id;
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', String(id));
    } catch {
      /* noop */
    }
  }, []);

  const onDragEnter = useCallback((e: React.DragEvent, overId: number) => {
    const dragId = dragIdRef.current;
    if (dragId == null || dragId === overId) return;
    e.preventDefault();
    setSelected((prev) => {
      const from = prev.indexOf(dragId);
      const to = prev.indexOf(overId);
      if (from === -1 || to === -1 || from === to) return prev;
      const next = [...prev];
      next.splice(from, 1);
      next.splice(to, 0, dragId);
      return next;
    });
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (dragIdRef.current == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDragEnd = useCallback(() => {
    dragIdRef.current = null;
    setDraggingId(null);
  }, []);

  // Done → persist only if the ordered ids changed (parity 57).
  const handleDone = useCallback(() => {
    const changed =
      selected.length !== initialSelected.length ||
      selected.some((id, i) => initialSelected[i] !== id);
    if (changed) onDone(selected);
    else onCancel();
  }, [selected, initialSelected, onDone, onCancel]);

  return (
    <div id="whiteboard_sett" className="popup-wrapper width-medium active">
      <div className="popup-content">
        <h2 className="popup-heading">Manage columns</h2>
        <ul className="two-heading">
          <li>
            <h3>Available columns</h3>
          </li>
          <li>
            <h3>Visible columns</h3>
          </li>
        </ul>
        <div className="popup-content-part v-brd two-cols-popup-content">
          {/* Left: available checkboxes. */}
          <ul>
            {available.map((col) => {
              const id = Number(col.id);
              return (
                <li key={id}>
                  <label htmlFor={`ch${id}`}>
                    <input
                      id={`ch${id}`}
                      type="checkbox"
                      name={String(id)}
                      checked={isChecked(id)}
                      onChange={() => toggle(id)}
                    />
                    <span className="label text-left">{col.name}</span>
                  </label>
                </li>
              );
            })}
          </ul>
          {/* Right: ordered, draggable visible list. */}
          <ul id="selected_columns">
            {selected.map((id) => (
              <li
                key={id}
                className={`clearfix drag${draggingId === id ? ' dragging' : ''}`}
                name={String(id)}
                draggable
                onDragStart={(e) => onDragStart(e, id)}
                onDragEnter={(e) => onDragEnter(e, id)}
                onDragOver={onDragOver}
                onDrop={(e) => e.preventDefault()}
                onDragEnd={onDragEnd}
              >
                <span>{nameById.get(id) ?? ''}</span>
                <i className="icon-two-lines" />
              </li>
            ))}
          </ul>
        </div>
        <div className="popup-note">
          Move up in the dialog in order to move left in the Dashboard, move down in the dialog in
          order to move right in the Dashboard.
        </div>
        <div className="popup-control-buttons">
          <button
            name="done"
            className="highlighted-btn"
            type="button"
            autoFocus
            onClick={handleDone}
          >
            Done
          </button>
          <button name="cancel" className="not-highlighted-btn" type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColumnSettingsModal;
